import apiClient from '@/lib/api/client';

// ── Offline submit queue ────────────────────────────────────────────────────
// POSTs that fail with a network error (dead-zone job sites) are parked in
// localStorage and replayed when the connection returns. Queue is per-device,
// same trust level as the stored auth tokens.

const KEY = 'op_offline_queue';

export interface QueuedPost {
  path: string;
  payload: unknown;
  queued_at: string;
}

const read = (): QueuedPost[] => {
  try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch { return []; }
};
const write = (q: QueuedPost[]) => localStorage.setItem(KEY, JSON.stringify(q));

export const queuedCount = () => read().length;

/** True when the failure is connectivity, not a server rejection. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isNetworkError = (err: any) => !!err && !err.response;

export const enqueue = (path: string, payload: unknown) => {
  write([...read(), { path, payload, queued_at: new Date().toISOString() }]);
};

let flushing = false;
export async function flushQueue(): Promise<{ sent: number; remaining: number }> {
  if (flushing) return { sent: 0, remaining: queuedCount() };
  flushing = true;
  let sent = 0;
  try {
    let q = read();
    while (q.length > 0) {
      const item = q[0];
      try {
        await apiClient.post(item.path, item.payload);
        sent++;
      } catch (err) {
        if (isNetworkError(err)) break; // still offline — stop, keep the rest
        // Server rejected it (bad data, auth) — drop it rather than loop forever
        console.error('Offline queue item rejected, dropping:', item.path, err);
      }
      q = q.slice(1);
      write(q);
    }
    return { sent, remaining: read().length };
  } finally {
    flushing = false;
  }
}

let inited = false;
export function initOfflineSync(onFlushed?: (sent: number) => void) {
  if (inited) return;
  inited = true;
  const run = () => {
    if (queuedCount() > 0) {
      flushQueue().then(r => { if (r.sent > 0) onFlushed?.(r.sent); });
    }
  };
  window.addEventListener('online', run);
  run();
}
